import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensChanges } from "@/services/queries/tokens/tokensScreenerQueries";
import { MetricKeys } from "@/constants/metrics";

type RangeKey = '1d' | '7d' | '30d' | '365d';

type Props = {
  rows: Array<Record<string, unknown>>;
  range: RangeKey;
  xKey: string;
  search: string;
  mcMin: number | '';
  mcMax: number | '';
  labelFromCatalog: (key: string) => string;
  height?: number | string;
};

const TreemapChart: React.FC<Props> = ({ rows, range, xKey, search, mcMin, mcMax, labelFromCatalog, height = 640 }) => {

  // Fetch changes for the selected metric (for color and labels)
  const changeMetricKeysCsv = useMemo(() => {
    const set = new Set<string>();
    if (xKey) set.add(xKey);
    return Array.from(set).join(',');
  }, [xKey]);
  
  const changesQuery = useQuery({
    queryKey: ["tokens", "changes", "treemap", changeMetricKeysCsv, range],
    queryFn: ({ signal }) => fetchTokensChanges({ windowsCsv: range, metricKeysCsv: changeMetricKeysCsv, sortBy: "market_cap", sortOrder: "desc", limit: 2000, signal }),
    staleTime: 60_000,
  });
  
  const changeByTickerMetric = useMemo(() => {
    const out = new Map<string, Map<string, number>>();
    const tokens = changesQuery.data?.tokens || [];
    for (const t of tokens) {
      const inner = new Map<string, number>();
      for (const m of (t.metrics || [])) {
        const raw = m?.windows?.[range]?.value;
        const pct = typeof raw === 'number' ? (raw * 100) : 0;
        inner.set(m.metric_key, pct);
      }
      out.set(t.ticker, inner);
    }
    return out;
  }, [changesQuery.data, range]);

  const getChangePct = React.useCallback((ticker: string, key: string): number => {
    const inner = changeByTickerMetric.get(ticker);
    if (!inner) return 0;
    return inner.get(key) ?? 0;
  }, [changeByTickerMetric]);

  const { option, count } = useMemo(() => {
    type Node = { name: string; size: number; colorVal: number; displayVal: number | null; ticker: string };
    const nodes: Node[] = [];
    
    for (const r of rows as Array<Record<string, unknown>>) {
      const ticker = String(r["tokenSymbol"] ?? "");
      if (!ticker) continue;
      
      if (search) {
        const name = String(r["tokenName"] ?? r["name"] ?? "");
        const s = search.toLowerCase();
        if (!ticker.toLowerCase().includes(s) && !name.toLowerCase().includes(s)) continue;
      }
      
      const mcRaw = (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null;
      const mc = mcRaw == null ? NaN : Number(mcRaw);
      if (!Number.isFinite(mc) || mc <= 0) continue;
      if (mcMin !== '' && mc < Number(mcMin)) continue;
      if (mcMax !== '' && mc > Number(mcMax)) continue;

      const displayVal = getChangePct(ticker, xKey);
      const colorVal = Number(displayVal ?? 0);
      const name = String(r["tokenName"] ?? ticker);
      nodes.push({ name, size: mc, colorVal: Number(colorVal) || 0, displayVal: displayVal ?? null, ticker });
    }

    const fmtCurrencyShort = (val: number) => {
      const a = Math.abs(val);
      if (a >= 1e12) return `$${(val/1e12).toFixed(1)}T`;
      if (a >= 1e9) return `$${(val/1e9).toFixed(1)}B`;
      if (a >= 1e6) return `$${(val/1e6).toFixed(1)}M`;
      if (a >= 1e3) return `$${(val/1e3).toFixed(1)}K`;
      if (a >= 1) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
      return `$${val.toLocaleString(undefined, { maximumFractionDigits: 6 })}`;
    };
    
    const fmtPercent = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
    const metricLabel = labelFromCatalog(String(xKey));

    const seriesData = nodes.map((n) => ({
      name: n.ticker,
      value: [Math.max(0, n.size), n.colorVal],
      displayVal: n.displayVal,
      ticker: n.ticker,
      fullName: n.name,
    }));

    // Robust symmetric scaling around 0 with outlier clipping by percentile
    const absVals = nodes.map((n) => Math.abs(n.colorVal)).filter((v) => Number.isFinite(v));
    const percentile = (arr: number[], p: number) => {
      if (arr.length === 0) return 0;
      const sorted = [...arr].sort((a, b) => a - b);
      const idx = Math.floor((sorted.length - 1) * p);
      return sorted[idx];
    };
    const p95 = percentile(absVals, 0.95);
    const clipCap = Math.max(5, Math.min(40, p95 || 10));

    const option: EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        borderColor: "#374151",
        textStyle: { color: "#f3f4f6", fontSize: 12 },
        formatter: (params: unknown) => {
          const p = params as {
            name: string;
            value: number[];
            data: { fullName?: string; displayVal?: number | null; ticker?: string };
          };
          // Don't show tooltip for gaps/empty areas
          if (!p.data?.ticker || !p.value?.[0]) return '';
          const change = p.value?.[1] ?? 0;
          const changeColor = change >= 0 ? "#22c55e" : "#ef4444";
          return `<div style="font-weight:600;margin-bottom:4px;">${p.data?.fullName || p.name}</div>
            <div>Market Cap: ${fmtCurrencyShort(p.value?.[0] ?? 0)}</div>
            <div style="color:${changeColor};">${metricLabel} (${range}): ${fmtPercent(change)}</div>`;
        },
      },
      visualMap: {
        show: false,
        min: -clipCap,
        max: clipCap,
        inRange: { color: ['#7f1d1d', '#dc2626', '#22c55e', '#16a34a'] },
        dimension: 1,
      },
      series: [
        {
          type: 'treemap',
          data: seriesData,
          roam: false,
          nodeClick: false,
          visualDimension: 1,
          breadcrumb: { show: false },
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          label: {
            show: true,
            color: '#f8fafc',
            fontSize: 13,
            overflow: 'truncate',
            formatter: (params: unknown) => {
              const p = params as { name: string; value: number[] };
              const change = p.value?.[1] ?? 0;
              return `{name|${p.name}}\n{change|${fmtPercent(change)}}`;
            },
            rich: {
              name: {
                fontSize: 13,
                fontWeight: 600,
                color: "#ffffff",
                textShadowColor: "rgba(0,0,0,0.5)",
                textShadowBlur: 2,
              },
              change: {
                fontSize: 11,
                color: "#ffffffcc",
                textShadowColor: "rgba(0,0,0,0.5)",
                textShadowBlur: 2,
              },
            },
          },
          itemStyle: {
            borderColor: 'rgba(15,23,42,0.35)',
            borderWidth: 1,
            gapWidth: 1,
            borderRadius: 4,
          },
          levels: [
            {
              itemStyle: {
                borderWidth: 0,
                gapWidth: 2,
              },
            },
          ],
        }
      ],
    } as EChartsOption;

    return { option, count: seriesData.length };
  }, [rows, search, mcMin, mcMax, xKey, range, labelFromCatalog, getChangePct]);

  const isLoading = changesQuery.isLoading;
  const error = (changesQuery.error as Error | null | undefined)?.message || null;
  
  return (
    <EChartsRenderer
      chartOption={option}
      height={height}
      isLoading={isLoading}
      error={error}
      hasData={count > 0}
      containerClassName="bg-transparent border-0 rounded-lg overflow-hidden"
    />
  );
};

export default TreemapChart;
